// Loyalty payment presentation — the ONE place any surface works out how
// a completed order was actually paid for (Sep 2026).
//
// The problem this exists to solve: a loyalty redemption is a TENDER, not
// a discount. `order.total` stays the full price of the order — $2.17 for
// RUD-ORD-000047 — and that is correct: it is what the food cost, it is
// what tax was charged on, and it is what the accounting total must keep
// saying. But only $1.17 of it was ever handed over at the counter; the
// other $1.00 was settled in points.
//
// Every receipt and payment summary that printed `TOTAL $2.17` next to
// `Paid via CASH` was therefore telling the customer they had paid $2.17
// in cash, which they had not. The fix is never to rewrite the total —
// it is to show the three figures separately, the way the backend has
// always stored them.
//
// The arithmetic here is a deliberate mirror of
// `services/loyalty_rules.py::external_payable_cents`:
//
//     external = max(0, gross_total_cents - persisted_loyalty_tender_cents)
//
// Two rules make historical receipts correct:
//
//   * It reads the order's OWN persisted loyalty snapshot
//     (`loyalty.tender_cents`, `loyalty.points_redeemed`). It never
//     consults the member's current balance and never re-derives the
//     amount from the tenant's CURRENT loyalty rule — the rule may have
//     changed many times since the order was placed, and a receipt must
//     reprint identically for ever.
//   * An ABSENT loyalty block means zero tender. That is the ordinary
//     non-member case and the case of every order placed before loyalty
//     existed, so those receipts render exactly as they always did.
//     A PRESENT but unreadable value is a different thing and fails
//     closed (`null`) — a receipt never invents a payment figure, and
//     never shows a negative one.

/** Cents from a stored dollar amount. `null` when it is not a number. */
function dollarsToCents(value) {
  if (value == null || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return Math.round(num * 100);
}

/** The order's gross total in cents — the full price, loyalty included. */
export function grossTotalCents(orderDoc) {
  if (!orderDoc || typeof orderDoc !== "object") return null;
  const direct = dollarsToCents(orderDoc.total);
  if (direct != null) return direct;
  return dollarsToCents((orderDoc.totals || {}).total);
}

/**
 * The loyalty value persisted against this order, in cents.
 *
 * `0` for an order with no loyalty block (the overwhelming majority).
 * `null` — fail closed — when a block exists but carries a value that
 * cannot be read as a number.
 */
export function loyaltyTenderCents(orderDoc) {
  if (!orderDoc || typeof orderDoc !== "object") return null;
  const raw = (orderDoc.loyalty || {}).tender_cents;
  if (raw == null || raw === "") return 0;
  const num = Number(raw);
  if (!Number.isFinite(num)) return null;
  return Math.max(0, Math.round(num));
}

/** Points the member spent on this order, or `null` when unknown. */
export function loyaltyPointsRedeemed(orderDoc) {
  if (!orderDoc || typeof orderDoc !== "object") return null;
  const raw = (orderDoc.loyalty || {}).points_redeemed;
  if (raw == null || raw === "") return null;
  const num = Number(raw);
  if (!Number.isFinite(num)) return null;
  return Math.max(0, Math.round(num));
}

/**
 * The money that actually changed hands externally — what staff
 * collected, what the card was charged, and the ceiling on what can ever
 * be refunded in money. Never negative, never a fallback to the gross
 * total, `null` when it cannot be established.
 */
export function externalPaidCents(orderDoc) {
  const total = grossTotalCents(orderDoc);
  if (total == null) return null;
  const tender = loyaltyTenderCents(orderDoc);
  if (tender == null) return null;
  return Math.max(0, total - tender);
}

/**
 * The complete allocation a receipt or payment summary renders.
 *
 * Returns `null` when the order carries no usable total, so callers can
 * fail closed rather than print a figure they cannot stand behind.
 */
export function orderPaymentBreakdown(orderDoc) {
  const totalCents = grossTotalCents(orderDoc);
  if (totalCents == null) return null;
  const tenderCents = loyaltyTenderCents(orderDoc);
  if (tenderCents == null) return null;
  // A tender larger than the order is not arithmetic we invent our way
  // out of: clamp it to the order so the external figure is never
  // negative, and report the clamped value so the two always reconcile.
  const appliedTenderCents = Math.min(tenderCents, totalCents);
  return {
    totalCents,
    loyaltyTenderCents: appliedTenderCents,
    pointsRedeemed: loyaltyPointsRedeemed(orderDoc),
    externalCents: totalCents - appliedTenderCents,
    balanceDueCents: 0,
    hasLoyalty: appliedTenderCents > 0,
  };
}

/**
 * What to call the money that was actually collected. Never "Total" —
 * the whole defect was labelling the gross total as cash.
 */
export function externalPaidLabel(paymentMethod) {
  const pm = String(paymentMethod || "").toLowerCase();
  if (pm === "cash") return "Cash paid";
  if (pm === "card" || pm.startsWith("stripe") || pm.startsWith("clover")) return "Card paid";
  return "Amount paid";
}

/** `Loyalty points redeemed (1)`, degrading gracefully without a count. */
export function loyaltyRedeemedLabel(points) {
  if (points == null || !Number.isFinite(Number(points)) || Number(points) <= 0) {
    return "Loyalty points redeemed";
  }
  return `Loyalty points redeemed (${Math.round(Number(points))})`;
}
